/**
 * RecapBar — the one-line "where this chat is at" above the composer.
 *
 * Sibling of the sidebar's recap line (ADR-0018), for the case the sidebar can't
 * help with: you've come back to a chat whose last reply is a wall of text, and
 * you need to know where things stand before you can answer. The sidebar caption
 * is invisible once you're reading the conversation, so it's repeated here, right
 * where you're about to type.
 *
 * Only renders when it EARNS the space. A recap of a two-line reply just repeats
 * the message immediately above it, which is pure chrome — so the bar is gated on
 * the last assistant message actually being long enough to be worth summarising.
 * That mirrors (but is independent of) the backend gate that decides whether to
 * spend a model call: this one is about whether the UI is useful, not about cost.
 */

import { useChatStore } from "@/stores/chatStore";
import type { Message } from "@/api/types";

/**
 * Minimum length of the last assistant reply before a recap is worth showing.
 *
 * ~2 rendered lines in the `max-w-3xl` message column. A recap is capped at 90
 * characters, so captioning anything much shorter than this saves the reader
 * nothing — they'd scan the same words twice. Kept in step with
 * ``RECAP_MIN_SOURCE_CHARS`` in ``cowork/agents/recap_runner.py``.
 */
const MIN_BLOB_CHARS = 200;

/** Concatenated ``text`` blocks of a persisted message body, or "". */
function textOf(msg: Message): string {
  try {
    const blocks = JSON.parse(msg.content_json);
    if (typeof blocks === "string") return blocks;
    if (!Array.isArray(blocks)) return "";
    return blocks
      .filter((b: { type?: string }) => b?.type === "text")
      .map((b: { text?: string }) => b.text ?? "")
      .join(" ")
      .trim();
  } catch {
    return "";
  }
}

export function RecapBar() {
  const recap = useChatStore((s) =>
    s.conversations.find((c) => c.id === s.activeConversationId)?.recap ?? null,
  );
  // No sub-agent filter needed: every backend read path that feeds the transcript
  // already filters ``parent_tool_use_id IS NULL`` (v35), so the store only ever
  // holds rows the user can actually see — which is why the field isn't even on
  // the frontend ``Message`` type.
  const lastAssistantLength = useChatStore((s) => {
    for (let i = s.messages.length - 1; i >= 0; i--) {
      const m = s.messages[i]!;
      if (m.role === "assistant") return textOf(m).length;
    }
    return 0;
  });

  // Deliberately NOT gated on ``isAgentRunning``, though the recap does describe
  // the previous turn while one streams.
  //
  // Gating on it made the bar appear the instant a turn ended, which grows the
  // composer and shrinks Virtuoso's scroll viewport by a row's height at exactly
  // the moment the user is reading the final answer. This component sits OUTSIDE
  // the virtualised list (ChatView renders MessageList and ChatInput as
  // siblings), so it can't disturb item keys, `firstItemIndex`, or the Footer
  // slot — but the composer's height is still the scroller's height, and a
  // mid-read jump is exactly the kind of scroll regression this list has a long
  // history of. Staying mounted across the turn boundary keeps that height
  // constant; only the TEXT changes, and the line is truncated to one row so it
  // can't reflow.
  if (!recap || lastAssistantLength < MIN_BLOB_CHARS) return null;

  return (
    <div
      className="mb-2 flex items-baseline gap-2 rounded-lg border border-space-30
                 bg-surface px-3 py-1.5 text-xs"
      title={recap}
    >
      <span className="shrink-0 font-medium text-space-50">Recap</span>
      <span className="min-w-0 flex-1 truncate italic text-space-50">{recap}</span>
    </div>
  );
}
